import { isHttpClientError } from "../../../lib/http/errors";
import type { DrivingEndpoint, DrivingRequest, DrivingResult, ItineraryNode, WalkingResult } from "../types";

type LocalTransportQueries = {
  getDrivingRoute: (request: DrivingRequest, signal?: AbortSignal) => Promise<DrivingResult>;
  getWalkingRoute: (request: DrivingRequest, signal?: AbortSignal) => Promise<WalkingResult>;
};

export type LocalTransportMeasurement =
  | { mode: "bus"; result: DrivingResult }
  | { mode: "walk"; result: WalkingResult };

/** 自动比较大巴与步行后，供编辑器解释最终选择的测算过程。 */
export type LocalTransportAutomaticSelection =
  | { kind: "bus_to_walk"; busDistanceKm: number; walkingDistanceKm: number }
  | { kind: "walk_to_bus"; busDistanceKm: number; walkingDistanceKm: number };

export type LocalTransportMeasurementWithNotice = LocalTransportMeasurement & {
  automaticSelection?: LocalTransportAutomaticSelection;
};

/** 构造本地交通端点并保留明确交通类型，仅名称一致时保留主数据引用。 */
export function localTransportEndpoint(node: ItineraryNode, input = node.title): DrivingEndpoint {
  const name = input.trim();
  const endpoint: DrivingEndpoint = { name };
  if (node.nodeType === "railway_station" || node.nodeType === "airport") endpoint.transportKind = node.nodeType;
  if (name !== node.title.trim()) return endpoint;
  if (node.pointSpuId) return { ...endpoint, refType: "point_spu", refId: node.pointSpuId };
  if (node.stayAreaId) return { ...endpoint, refType: "stay_area", refId: node.stayAreaId };
  if (node.organizationSiteId) return { ...endpoint, refType: "organization_site", refId: String(node.organizationSiteId) };
  return endpoint;
}

/** 保留已证实的端点诊断；未知路线失败不得推断为数据库坐标错误。 */
export function localTransportErrorMessage(reason: unknown, request: DrivingRequest): string {
  const route = `${request.origin.name || "起点"} → ${request.destination.name || "终点"}`;
  const fallback = "交通测算失败，请重试或手工填写距离和时长。";
  if (!isHttpClientError(reason)) return `${route}：${fallback}`;
  const body = reason.responseBody;
  const data = body && typeof body === "object" ? Reflect.get(body, "data") : null;
  const provider = data && typeof data === "object" ? Reflect.get(data, "provider_diagnostic") : null;
  if (["ITINERARY_TRANSPORT_UPSTREAM", "ITINERARY_TRANSPORT_TIMEOUT"].includes(reason.code ?? "")
    && provider && typeof provider === "object" && Reflect.get(provider, "provider") === "deepseek"
    && ["request", "response", "output"].includes(Reflect.get(provider, "stage"))) {
    return reason.message;
  }
  if (reason.code === "ITINERARY_TRANSPORT_EMPTY"
    && data && typeof data === "object" && Reflect.get(data, "endpoint_diagnostic") === true) {
    return reason.message;
  }
  const messages: Record<string, string> = {
    ITINERARY_TRANSPORT_EMPTY: "没有返回可用路线，请核对起终点或手工填写距离和时长。",
    ITINERARY_TRANSPORT_TIMEOUT: "交通测算超时，请稍后重试。",
    ITINERARY_TRANSPORT_UPSTREAM: "交通测算服务返回异常，请稍后重试。",
    ITINERARY_TRANSPORT_CONFIG_MISSING: "交通测算服务未配置，请联系管理员。",
    ITINERARY_TRANSPORT_INVALID: "交通查询条件无效，请重新选择起终点。",
    ITINERARY_REFERENCE_INVALID: "起点或终点已失效或停用，请重新选择。",
    ITINERARY_TRANSPORT_WALKING_OUT_OF_RANGE: "超出步行测算范围，请改用大巴或手工填写距离和时长。",
    ECONNABORTED: "交通测算请求超时，请稍后重试。",
    ETIMEDOUT: "交通测算请求超时，请稍后重试。",
    ERR_NETWORK: "交通测算请求未能连接服务，请检查网络后重试。",
  };
  const message = messages[reason.code ?? ""] ?? (reason.statusCode === null
    ? messages.ERR_NETWORK : fallback);
  return `${route}：${message}`;
}

/** 大巴不足 800 米优先采用步行；两种测算互为失败兜底，取消不触发后续请求。 */
export async function queryLocalTransport(
  request: DrivingRequest,
  queries: LocalTransportQueries,
  signal?: AbortSignal,
  preferredMode: "bus" | "walk" = "bus",
): Promise<LocalTransportMeasurementWithNotice> {
  const query = async (mode: "bus" | "walk"): Promise<LocalTransportMeasurement> => {
    signal?.throwIfAborted();
    const measurement: LocalTransportMeasurement = mode === "bus"
      ? { mode, result: await queries.getDrivingRoute(request, signal) }
      : { mode, result: await queries.getWalkingRoute(request, signal) };
    signal?.throwIfAborted();
    const result = measurement.result;
    if (!result || !Number.isFinite(result.distanceKm) || result.distanceKm < 0
      || !Number.isFinite(result.businessDurationMinutes) || result.businessDurationMinutes < 0) {
      throw new Error("暂无可用交通路线");
    }
    return measurement;
  };
  let first: LocalTransportMeasurement | null = null;
  let firstError: unknown;
  try {
    first = await query(preferredMode);
  } catch (error) {
    if (signal?.aborted) throw error;
    firstError = error;
  }
  if (first && (first.mode === "walk" || first.result.distanceKm >= 0.8)) return first;
  try {
    const fallback = await query(preferredMode === "bus" ? "walk" : "bus");
    // 只有步行本身也属于 800 米内的短途，才自动切换；长步行路线保留大巴结果。
    if (
      first?.mode === "bus"
      && fallback.mode === "walk"
      && fallback.result.distanceKm >= 0.8
    ) {
      return {
        ...first,
        automaticSelection: {
          kind: "walk_to_bus",
          busDistanceKm: first.result.distanceKm,
          walkingDistanceKm: fallback.result.distanceKm,
        },
      };
    }
    if (first?.mode === "bus" && fallback.mode === "walk") {
      return {
        ...fallback,
        automaticSelection: {
          kind: "bus_to_walk",
          busDistanceKm: first.result.distanceKm,
          walkingDistanceKm: fallback.result.distanceKm,
        },
      };
    }
    return fallback;
  } catch (error) {
    if (signal?.aborted) throw error;
    if (!first) throw firstError ?? error;
    return first;
  }
}
